import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const AnalysisConfigsList = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    enable_auto_summary: false,
    include_sentiment_analysis: false,
    extract_key_topics: false,
    extract_action_items: false,
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Fetch all analysis configs
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/analysis-configs");
      setConfigs(response.data || []);
    } catch (err) {
      toast.error("Failed to fetch analysis configs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Create new analysis config
  const handleCreate = async () => {
    if (!formData.name) return toast.error("Name is required");
    setLoading(true);
    try {
      await apiClient.post("/analysis-configs", formData);
      toast.success("Analysis config created successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to create analysis config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Edit existing config
  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      enable_auto_summary: config.enable_auto_summary,
      include_sentiment_analysis: config.include_sentiment_analysis,
      extract_key_topics: config.extract_key_topics,
      extract_action_items: config.extract_action_items,
    });
    setOpenModal(true);
  };

  // Update existing config
  const handleUpdate = async () => {
    if (!editingConfig) return;
    setLoading(true);
    try {
      await apiClient.put(`/analysis-configs/${editingConfig._id}`, formData);
      toast.success("Analysis config updated successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to update analysis config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete config
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this analysis config?"))
      return;
    setLoading(true);
    try {
      await apiClient.delete(`/analysis-configs/${id}`);
      toast.success("Analysis config deleted successfully");
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to delete analysis config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingConfig(null);
    setFormData({
      name: "",
      enable_auto_summary: false,
      include_sentiment_analysis: false,
      extract_key_topics: false,
      extract_action_items: false,
    });
  };

  const columns = [
    { accessorKey: "name", header: "Name" },
    {
      accessorKey: "enable_auto_summary",
      header: "Auto Summary",
      Cell: ({ cell }) => (
        <Switch checked={cell.getValue()} disabled size="small" />
      ),
    },
    {
      accessorKey: "include_sentiment_analysis",
      header: "Sentiment",
      Cell: ({ cell }) => (
        <Switch checked={cell.getValue()} disabled size="small" />
      ),
    },
    {
      accessorKey: "extract_key_topics",
      header: "Key Topics",
      Cell: ({ cell }) => (
        <Switch checked={cell.getValue()} disabled size="small" />
      ),
    },
    {
      accessorKey: "extract_action_items",
      header: "Action Items",
      Cell: ({ cell }) => (
        <Switch checked={cell.getValue()} disabled size="small" />
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && configs.length === 0) return <PageLoader />;

  return (


    <>
    
    {/* Header */}
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <h3>Analysis Configs</h3>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setOpenModal(true)}
      >
        New Config
      </Button>
    </div>
    
    <Box sx={{ p: 3 }}>  
      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search analysis configs..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={configs.filter((c) =>
          c.name?.toLowerCase().includes(searchText.toLowerCase())
        )}
        enableRowActions={false}
        enableColumnActions={false}
        enableSorting
        enablePagination
        initialState={{
          pagination: { pageSize: 10, pageIndex: 0 },
        }}
      />

      {/* Popup Form */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingConfig
            ? "Edit Analysis Config"
            : "Create New Analysis Config"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="NAME"
              name="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              {[
                {
                  key: "enable_auto_summary",
                  label: "Enable Auto Summary",
                },
                {
                  key: "include_sentiment_analysis",
                  label: "Include Sentiment Analysis",
                },
                {
                  key: "extract_key_topics",
                  label: "Extract Key Topics",
                },
                {
                  key: "extract_action_items",
                  label: "Extract Action Items",
                },
              ].map((opt) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={opt.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography>{opt.label}</Typography>
                  <Switch
                    checked={formData[opt.key]}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        [opt.key]: e.target.checked,
                      }))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingConfig ? handleUpdate : handleCreate}
          >
            {editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    
    </>


  );
};

export default AnalysisConfigsList;
